/**
 * A SALA SEM INTERNET PREPARADA SOZINHA (02/11/2026; aplicativo do computador 2.0.20 e app do celular 1.6). Sem tela
 * própria: com o portal aberto NO APLICATIVO, com internet, num aparelho marcado como "aplica salas sem internet", a cada
 * 10 minutos (e ao abrir, e ao voltar a internet) pergunta ao portal o que preparar (`/aplicativo/para-preparar`: os
 * simulados com "Vou aplicar em sala sem internet" e início marcado) e prepara — ou atualiza — o que mudou: a versão do
 * pacote, ou o início. A sala que já abriu neste aparelho não é tocada (a prova pode estar correndo).
 *
 * E O ENVIO AO ABRIR O APP (celular 1.6): se o motor deixou o marcador "há envio pendente", liga o motor e envia; no
 * Android, agenda o envio com o app fechado (WorkManager). No iPhone/iPad é o único jeito: o sistema não roda o motor com o
 * app fechado, e o push "sala sem envio" é o lembrete.
 *
 * O cartão do simulado mostra "Atualizando…" enquanto um preparo corre (`useEmPreparo`).
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { App } from '@capacitor/app';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { api } from '@/utils/api';
import { SalaNoCelular } from 'sala-no-celular';
import { ligarMotor, controle, salaNoCelularDisponivel } from '@/features/salaNoCelular/salaNoCelular';
import { ponteDoAplicativo } from './aplicativo';
import { aparelhoDaSala, prepararSozinho, salasDoAparelho } from './salaNoAparelho';
import { celularProtegido } from './contaDaSala';

const INTERVALO_MS = 10 * 60 * 1000;

// Os simulados em preparo agora, para o cartão dizer "Atualizando…".
const emPreparo = new Set<number>();
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach((f) => f());
function assinar(f: () => void) { ouvintes.add(f); return () => { ouvintes.delete(f); }; }
export function useEmPreparo(simulationId: number): boolean {
  return useSyncExternalStore(assinar, () => emPreparo.has(simulationId), () => false);
}
// Por que o aparelho NÃO preparou sozinho (02/11/2026) — o cartão explica e oferece preparar à mão. 03/11/2026: também
// "o motor da sala não respondeu" (`sem-motor`) e a falha do pedido, com a frase real (`falhou:<frase>`), em vez de silêncio.
export type MotivoSemPreparo = 'sem-bloqueio' | 'app-antigo' | 'sem-motor' | `falhou:${string}`;
const semPreparo = new Map<number, MotivoSemPreparo>();
export function useMotivoSemPreparo(simulationId: number): MotivoSemPreparo | null {
  return useSyncExternalStore(assinar, () => semPreparo.get(simulationId) ?? null, () => null);
}

interface ParaPreparar { id: number; titulo: string; inicio: string; versao: string }

/** Uma passada: o que preparar, comparado com o que este aparelho guarda. Devolve quantos prepararam. */
export async function passadaDoPreparo(): Promise<number> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 0;
  /*
   * EM TODO APARELHO DO PROFESSOR (02/11/2026, decisão do pesquisador): primeiro o portal diz se há o que preparar (as turmas
   * dele — o servidor confere que é professor da turma); sem nada, o motor do celular nem liga. A chave de Minha conta só
   * DESLIGA (`aplicaSalas: false`). No celular, só com bloqueio de tela: sem ele, o cartão explica e oferece preparar à mão.
   */
  const { data } = await api.get<{ simulados: ParaPreparar[] }>('/api/offline-rooms/aplicativo/para-preparar');
  if (!data.simulados?.length) return 0;
  const aparelho = await aparelhoDaSala();
  if (!aparelho) {
    // No celular, aparelho nulo = o motor não respondeu (no computador, a ponte antiga não tem aparelho: nada a dizer).
    if (!ponteDoAplicativo()) { for (const s of data.simulados) semPreparo.set(s.id, 'sem-motor'); avisar(); }
    return 0;
  }
  if (!aparelho.aplicaSalas) return 0;
  if (!ponteDoAplicativo()) {
    const protegido = await celularProtegido();
    if (protegido !== true) {
      for (const s of data.simulados) semPreparo.set(s.id, protegido === false ? 'sem-bloqueio' : 'app-antigo');
      avisar();
      return 0;
    }
  }
  const salas = await salasDoAparelho();
  let n = 0;
  for (const s of data.simulados ?? []) {
    const daqui = salas.filter((x) => x.simuladoId === s.id && !x.pacoteApagadoEm).sort((a, b) => String(b.preparadaEm).localeCompare(String(a.preparadaEm)))[0];
    if (daqui?.estado) continue; // já abriu aqui: não se atualiza sozinha
    if (daqui && daqui.versao === s.versao && daqui.inicio && Date.parse(daqui.inicio) === Date.parse(s.inicio)) continue;
    emPreparo.add(s.id); avisar();
    try { await prepararSozinho(s.id); n++; semPreparo.delete(s.id); }
    catch (e) { console.warn(`[preparo automático] #${s.id}:`, (e as Error).message); semPreparo.set(s.id, `falhou:${(e as Error).message}`); }
    finally { emPreparo.delete(s.id); avisar(); }
  }
  return n;
}

/** O envio pendente ao abrir o app do celular (1.6). O app de antes não tem `haEnvioPendente`: nada acontece. */
async function enviarAoAbrir(): Promise<void> {
  if (ponteDoAplicativo() || !salaNoCelularDisponivel()) return;
  const r = await SalaNoCelular.haEnvioPendente().catch(() => null);
  if (!r?.pendente) return;
  await ligarMotor();
  await controle('POST', '/enviar-pendentes', {});
  if (r.emSegundoPlano) await SalaNoCelular.agendarEnvio().catch(() => { /* sem WorkManager: envia ao abrir */ });
}

export function PreparoAutomatico() {
  const { user, isAuthenticated } = useAuth();
  const papel = (user as { role?: string } | null)?.role;
  const qc = useQueryClient();
  const rodando = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !['TEACHER', 'SCHOOL_STAFF', 'ADMIN'].includes(String(papel))) return;
    if (!ponteDoAplicativo() && !salaNoCelularDisponivel()) return;
    const rodar = async () => {
      if (rodando.current) return;
      rodando.current = true;
      try {
        await enviarAoAbrir().catch(() => { /* sem internet: o marcador fica, e tenta na próxima */ });
        if (await passadaDoPreparo().catch(() => 0)) void qc.invalidateQueries({ queryKey: ['salas-do-aparelho'] });
      } finally { rodando.current = false; }
    };
    const primeira = setTimeout(rodar, 5000);
    const vez = setInterval(rodar, INTERVALO_MS);
    window.addEventListener('online', rodar);
    const volta = App.addListener('resume', () => { void rodar(); });
    return () => { clearTimeout(primeira); clearInterval(vez); window.removeEventListener('online', rodar); void volta.then((h) => h.remove()).catch(() => {}); };
  }, [isAuthenticated, papel, qc]);
  return null;
}
